import { useEffect, useState } from "react";
import { Plus, Pencil, Loader2, Phone, Car } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { EmptyState, PageHeader } from "../../components/Common";
import { api, formatApiError } from "../../lib/api";

const empty = { name: "", email: "", password: "", phone: "", vehicle: "", active: true, is_vendor: false };

function Field({ label, testId, ...props }) {
  return <div className="space-y-1.5"><Label>{label}</Label><Input {...props} data-testid={testId} /></div>;
}

function MemberDialog({ member, onClose, onSaved }) {
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const isEdit = !!member?.id;
  useEffect(() => { if (member) setForm(isEdit ? { ...empty, ...member, password: "" } : empty); }, [member, isEdit]);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (isEdit) {
        const { name, phone, vehicle, active, password, is_vendor } = form;
        await api.put(`/users/${member.id}`, { name, phone, vehicle, active, is_vendor: !!is_vendor, ...(password ? { password } : {}) });
      } else {
        await api.post("/users", { name: form.name, email: form.email, password: form.password, phone: form.phone, vehicle: form.vehicle, is_vendor: !!form.is_vendor });
      }
      toast.success("Akun team tersimpan");
      onClose();
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!member} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="member-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">{isEdit ? "Edit Driver" : "Akun Driver Baru"}</DialogTitle>
          <DialogDescription>Driver login memakai email & password ini.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <Field label="Nama" required value={form.name} onChange={set("name")} testId="member-name-input" />
          <Field label="Email" type="email" required disabled={isEdit} value={form.email} onChange={set("email")} testId="member-email-input" />
          <Field label={isEdit ? "Password baru (kosongkan jika tidak diubah)" : "Password (min. 6 karakter)"} type="text" required={!isEdit} minLength={6} value={form.password} onChange={set("password")} testId="member-password-input" />
          <div className="grid grid-cols-2 gap-3">
            <Field label="No. Telp" value={form.phone} onChange={set("phone")} testId="member-phone-input" />
            <Field label="Kendaraan / Plat" value={form.vehicle} onChange={set("vehicle")} testId="member-vehicle-input" />
          </div>
          <label className="flex items-center justify-between gap-3 rounded-lg border p-3">
            <span><span className="block text-sm font-medium">Vendor</span><span className="block text-[11px] text-slate-500">Bisa terima lebih dari 1 order per hari, pesan WA hanya berisi catatan</span></span>
            <Switch checked={!!form.is_vendor} onCheckedChange={(v) => setForm((f) => ({ ...f, is_vendor: v }))} data-testid="member-vendor-switch" />
          </label>
          {isEdit && (
            <label className="flex items-center justify-between rounded-lg border p-3"><span className="text-sm font-medium">Akun aktif</span><Switch checked={form.active} onCheckedChange={(v) => setForm((f) => ({ ...f, active: v }))} data-testid="member-active-switch" /></label>
          )}
          <Button type="submit" disabled={saving} className="w-full h-11 bg-emerald-600 hover:bg-emerald-700" data-testid="member-submit-btn">{saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Simpan</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminTeam() {
  const [members, setMembers] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/users").then((r) => setMembers(r.data));
  useEffect(() => { load(); }, []);

  return (
    <div>
      <PageHeader eyebrow="Akun" title="Team Driver">
        <Button onClick={() => setEditing({})} className="h-10 bg-emerald-600 hover:bg-emerald-700 font-semibold" data-testid="add-member-btn"><Plus className="h-4 w-4 mr-1" />Tambah Driver</Button>
      </PageHeader>
      {members.length ? (
        <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="member-list">
          {members.map((m, i) => (
            <div key={m.id} className={`rise rounded-2xl border bg-white p-5 ${m.active ? "border-slate-200" : "border-slate-200 opacity-60"}`} style={{ animationDelay: `${i * 40}ms` }} data-testid={`member-card-${m.id}`}>
              <div className="flex items-center gap-3">
                <div className="h-11 w-11 rounded-xl bg-slate-950 text-emerald-400 grid place-items-center font-heading font-bold">{m.name.slice(0, 2).toUpperCase()}</div>
                <div className="min-w-0 flex-1">
                  <p className="font-heading font-semibold text-slate-900 truncate">{m.name}</p>
                  <p className="text-xs text-slate-500 truncate">{m.email}</p>
                </div>
                {m.is_vendor && <span className="text-[10px] font-semibold uppercase px-2 py-1 rounded-full bg-violet-100 text-violet-700" data-testid={`vendor-badge-${m.id}`}>Vendor</span>}
                {!m.active && <span className="text-[10px] font-semibold uppercase px-2 py-1 rounded-full bg-slate-100 text-slate-500">Nonaktif</span>}
              </div>
              <div className="mt-4 space-y-1 text-sm text-slate-600">
                <p className="flex items-center gap-2"><Phone className="h-3.5 w-3.5 text-slate-400" />{m.phone || "—"}</p>
                <p className="flex items-center gap-2"><Car className="h-3.5 w-3.5 text-slate-400" />{m.vehicle || "—"}</p>
              </div>
              <Button variant="outline" size="sm" className="mt-4" onClick={() => setEditing(m)} data-testid={`edit-member-${m.id}`}><Pencil className="h-3.5 w-3.5 mr-1" />Edit</Button>
            </div>
          ))}
        </div>
      ) : (
        <EmptyState title="Belum ada driver" text="Buat akun untuk team driver Anda." testId="empty-members" />
      )}
      <MemberDialog member={editing} onClose={() => setEditing(null)} onSaved={load} />
    </div>
  );
}
